<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, required: true },
  items: { type: Array, default: () => [] },
  selectedValue: { type: [String, Number], default: '' },
  emptyMessage: { type: String, default: 'Nenhuma opção disponível.' },
});

const emit = defineEmits(['close', 'select']);

const itemValue = item => String(item?.value ?? '');

const itemKey = item =>
  item?.key || itemValue(item) || item?.label || item?.tag || 'item';

const selectItem = item => {
  if (item?.disabled) return;
  emit('select', item);
};
</script>

<template>
  <div>
    <div
      v-if="show"
      class="fixed inset-0 z-[120] grid place-items-end bg-black/30 p-0 sm:place-items-center sm:p-4"
      role="presentation"
      @click.self="emit('close')"
    >
      <section
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        class="max-h-[88vh] w-full overflow-hidden rounded-t-lais bg-white shadow-lais-card sm:max-w-[420px] sm:rounded-lais"
      >
        <header
          class="flex items-center justify-between gap-3 border-b border-lais-border px-4 py-3"
        >
          <h3
            class="font-redHatDisplay text-[18px] font-semibold text-lais-text"
          >
            {{ title }}
          </h3>
          <button
            type="button"
            class="grid size-9 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-lais-bg-soft hover:text-lais-text"
            :aria-label="`Fechar ${title}`"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </header>

        <div class="max-h-[64vh] overflow-y-auto p-3">
          <p
            v-if="!items.length"
            class="rounded-lais border border-lais-border bg-lais-bg-soft px-4 py-5 text-center text-[13px] text-lais-text-muted"
          >
            {{ emptyMessage }}
          </p>

          <div v-else class="grid gap-2">
            <button
              v-for="item in items"
              :key="itemKey(item)"
              type="button"
              class="flex min-h-12 w-full items-start justify-between gap-3 rounded-lais border px-3 py-2 text-left transition-colors"
              :class="[
                String(selectedValue) === itemValue(item)
                  ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                  : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary',
                item.disabled
                  ? 'cursor-not-allowed opacity-60 hover:border-lais-border hover:text-lais-text-body'
                  : '',
              ]"
              :aria-pressed="String(selectedValue) === itemValue(item)"
              :aria-disabled="item.disabled ? 'true' : 'false'"
              @click="selectItem(item)"
            >
              <span class="min-w-0">
                <span class="block font-semibold">
                  {{ item.label }}
                </span>
                <span
                  v-if="item.description"
                  class="mt-0.5 block text-[12px] leading-relaxed text-lais-text-muted"
                >
                  {{ item.description }}
                </span>
                <span
                  v-if="item.tag"
                  class="mt-1 inline-flex rounded-full bg-lais-gold/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-lais-text"
                >
                  {{ item.tag }}
                </span>
              </span>
              <span
                v-if="String(selectedValue) === itemValue(item)"
                class="i-lucide-check size-4 shrink-0"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
